/**
Author - Vikrant Singh
Git - git@github.com:vikrantSingh97
**/

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {ActionButton, ActionButtonSecondary} from 'screens/commons';
import {KycScreenMapping, KycFlowMapping} from 'types';
import IMAGES from 'constants/Images';

const propTypes = {
  kycData: PropTypes.object.isRequired,
  handleDigioInitialization: PropTypes.func.isRequired,
  handleGoBack: PropTypes.func.isRequired,
};

const PartnerKyc = ({kycData, handleDigioInitialization, handleGoBack}) => {
  const digiLocker = 'https://i.wlycdn.com/wealthy-kyc/digi-locker.svg';
  const selfieUpload = 'https://i.wlycdn.com/wealthy-kyc/selfie-upload.svg';
  const panUpload = 'https://i.wlycdn.com/wealthy-kyc/pan-upload.svg';

  const {stage, flow} = kycData;

  let title = 'DigiLocker KYC';
  let subTitle = 'Redirecting to Digilocker';
  let description = '';
  let imgPath = digiLocker;
  let goBackButtonText = 'Process Manually';

  switch (stage) {
    case KycScreenMapping.DIGILOCKER:
      title = 'DigiLocker KYC';
      subTitle = 'Redirecting to Digilocker';
      description = '';
      imgPath = digiLocker;
      break;
    case KycScreenMapping.UPLOAD_PAN:
      title = 'PAN Card';
      subTitle = 'PAN verification through manual upload';
      description = 'Upload the front of your PAN Card for verification';
      imgPath = panUpload;
      break;
    case KycScreenMapping.SELFIE:
      title = 'Capture Selfie';
      subTitle = 'For Identity Verification';
      description =
        "Please ensure you're in a well-lit area and your face is clearly visible. This helps us confirm your identity securely.";
      imgPath = selfieUpload;
      break;
    default:
      title = 'DigiLocker KYC';
      subTitle = 'Redirecting to Digilocker';
      description = '';
      imgPath = digiLocker;
      break;
  }

  return (
    <KycWrapper>
      <ContentSection>
        <KycImage className="mt-5">
          <img alt={title} className="kyc-image" src={imgPath} />
        </KycImage>

        {stage === KycScreenMapping.DIGILOCKER ? (
          <NoteContainer>
            <NoteTitle>Important Pointers:</NoteTitle>

            <PointWrapper>
              <BulletImage
                alt="bullet-image"
                src="https://i.wlycdn.com/wealthy-home-page/bullet-star.svg"
                loading="lazy"
              />
              <NoteDescription>
                Keep your Aadhaar number and mobile number linked with Aadhaar handy.
              </NoteDescription>
            </PointWrapper>

            <PointWrapper>
              <BulletImage
                alt="bullet-image"
                src="https://i.wlycdn.com/wealthy-home-page/bullet-star.svg"
                loading="lazy"
              />
              <NoteDescription>You will receive OTP on this number.</NoteDescription>
            </PointWrapper>

            <PointWrapper>
              <BulletImage
                alt="bullet-image"
                src="https://i.wlycdn.com/wealthy-home-page/bullet-star.svg"
                loading="lazy"
              />
              <NoteDescription>
                If you haven't set up your DigiLocker PIN, or if you've forgotten it, click on
                "Forgot security PIN" on the DigiLocker KYC screen.
              </NoteDescription>
            </PointWrapper>
          </NoteContainer>
        ) : (
          <Description>{description}</Description>
        )}
        <FooterSection>
          <ActionButton
            buttonText="Continue"
            callback={handleDigioInitialization}
            className="next-button"
          />
          {stage === KycScreenMapping.DIGILOCKER && flow === KycFlowMapping.PARTNER && (
            <>
              <ActionButtonSecondary
                buttonText={goBackButtonText}
                callback={() => handleGoBack(flow)}
                additionalStyles={{
                  background: 'white',
                  color: (props) => props.theme.colors.brand.primary,
                  border: (props) => `1px solid ${props.theme.colors.brand.primary}`,
                }}
              />
              <InfoText>
                <img
                  src={IMAGES.infoIconImage}
                  alt="info-icon"
                  title="Choose Manual option after clicking this button"
                />
                <p className="body-small">
                  If digiLocker fails or you face any issues, you can manually upload your ID proofs
                  instead
                </p>
              </InfoText>
            </>
          )}
        </FooterSection>
      </ContentSection>
    </KycWrapper>
  );
};

const KycWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  width: 100%;
  padding: ${(props) => props.theme.spacing.md};

  @media screen and (max-width: 768px) {
    padding: ${(props) => props.theme.spacing.sm};
  }

  .kyc-image {
    height: 10rem;
    width: 10rem;
    object-fit: contain;
  }
`;

const FooterSection = styled.div`
  width: 100%;
  max-width: 600px;
  padding: ${(props) => props.theme.spacing.md};
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  align-self: center;

  @media screen and (max-width: 768px) {
    padding: ${(props) => props.theme.spacing.sm};
  }
`;

const InfoText = styled.div`
  text-align: center;
  margin-top: ${(props) => props.theme.spacing.sm};
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  color: ${(props) => props.theme.colors.brand.primary};
  background-color: ${(props) => props.theme.colors.background.secondary};
  padding: ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  width: 100%;

  img {
    height: 1.25rem;
    margin-right: 8px;
  }
`;

const PointWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const BulletImage = styled.img`
  margin-right: ${(props) => props.theme.spacing.md};
  height: 1.25rem;
  margin-top: 0.5rem;
`;

const NoteContainer = styled.div`
  background-color: ${(props) => props.theme.colors.background.secondary};
  border-radius: ${(props) => props.theme.borderRadius.md};
  padding: ${(props) => props.theme.spacing.lg};
  width: 100%;
  margin: ${(props) => props.theme.spacing.md} 0;
  max-width: 600px;
`;

const NoteTitle = styled.div.attrs({
  className: 'body-small',
})``;

const NoteDescription = styled.div.attrs({
  className: 'body-small',
})`
  margin: 0;
`;

const KycImage = styled.div`
  margin: ${(props) => props.theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 10rem;
`;

const Description = styled.span.attrs({
  className: 'body-small',
})`
  margin: ${(props) => props.theme.spacing.md} 0 0;
  padding: 0;
  color: ${(props) => props.theme.colors.text.secondary};
  text-align: center;
`;

PartnerKyc.propTypes = propTypes;

export default PartnerKyc;
